import { Injectable } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, combineLatest, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';

import { baseEnvironment } from '../../../environments/environment.base';
import {
  DataOnlyGQL,
  ListUsersGQL,
  ZoneDir,
} from '../../../generated/graphql.generated';
import { GraphQLModule } from '../../core/public-api';
import { DemoModeService } from '../../services/demo-mode.service';
import { currentTimeSeconds } from '../../time';
import { getDashboardErrorMessage } from '../../utilities/errors.util';
import { parseEmbeddedUrl, parseHyperlink } from '../../utilities/generic.util';

import {
  buildHiddenRowsNote,
  buildHiddenRowsTooltip,
  isFormerStaffName,
  isLeftOutOfMean,
  isStaffEmail,
} from '../dashboard-sales-leaderboard/sales-leaderboard-hidden-rows';
import {
  DashboardStore,
  SALES_LEADERBOARD_COLUMNS,
  SalesLeaderboardEntry,
  SalesLeaderboardRole,
} from '../dashboardv2.store';

// ---------------------------------------------------------------------------
// Constants (SL-internal, not re-exported)
// ---------------------------------------------------------------------------

const SALES_LEADERBOARD_DATA_COLUMNS = [
  'eventCount', 'cancelledEventCount',
  'estimateCount',
  'connectedLeadCount', 'connectedQualifiedLeadCount',
  'discountedSubTotal',
];

const SALES_LEADERBOARD_LEADS_DATA_COLUMNS = [
  'jobCount', 'connectedQualifiedBookingCount',
];

const SALES_LEADERBOARD_REVENUE_DATA_COLUMNS = [
  'discountedSubTotal', 'subTotal', 'discountTotal',
];

const SALES_LEADERBOARD_ESTIMATED_DATA_COLUMNS = [
  'discountedSubTotal', 'jobCount',
];

const SALES_LEADERBOARD_BOOKED_DATA_COLUMNS = [
  'discountedSubTotal', 'subTotal', 'discountTotal',
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface ReportData {
  headers: string[];
  rows: string[][];
}

interface SalesLeaderboardState {
  salesLeaderboard: ReportData;
  salesLeaderboardLoading: boolean;
  salesLeaderboardError: string | null;
  salesLeaderboardLeads: ReportData;
  salesLeaderboardLeadsLoading: boolean;
  salesLeaderboardLeadsError: string | null;
  salesLeaderboardCancellation: ReportData;
  salesLeaderboardCancellationLoading: boolean;
  salesLeaderboardCancellationError: string | null;
  salesLeaderboardRevenue: ReportData;
  salesLeaderboardRevenueLoading: boolean;
  salesLeaderboardRevenueError: string | null;
  salesLeaderboardEstimated: ReportData;
  salesLeaderboardEstimatedLoading: boolean;
  salesLeaderboardEstimatedError: string | null;
  salesLeaderboardBooked: ReportData;
  salesLeaderboardBookedLoading: boolean;
  salesLeaderboardBookedError: string | null;
  salesLeaderboardBookedCount: ReportData;
  salesLeaderboardBookedCountLoading: boolean;
  salesLeaderboardBookedCountError: string | null;
  salesLeaderboardBookedByEstimate: ReportData;
  salesLeaderboardBookedByEstimateLoading: boolean;
  salesLeaderboardBookedByEstimateError: string | null;
  // Requirement: ticket 7056 - the rows' emails, which pick out the staff rows to hide and the bots the Mean
  // leaves out. Null until loaded or if the lookup fails (then the error is set). `For` holds the user ids
  // (see `joinedUserIds`) the last finished lookup covered, so emails for an older set of people never count.
  salesLeaderboardUserEmails: Record<string, string> | null;
  // Requirement: ticket 7056 review - the rows' deleted accounts, from the same lookup and null alongside the emails.
  salesLeaderboardDeletedUserIds: string[] | null;
  salesLeaderboardUserEmailsError: string | null;
  salesLeaderboardUserEmailsFor: string | null;
}

const emptyReport = (): ReportData => ({ headers: [], rows: [] });

const initialState: SalesLeaderboardState = {
  salesLeaderboard: emptyReport(),
  salesLeaderboardLoading: false,
  salesLeaderboardError: null,
  salesLeaderboardLeads: emptyReport(),
  salesLeaderboardLeadsLoading: false,
  salesLeaderboardLeadsError: null,
  salesLeaderboardCancellation: emptyReport(),
  salesLeaderboardCancellationLoading: false,
  salesLeaderboardCancellationError: null,
  salesLeaderboardRevenue: emptyReport(),
  salesLeaderboardRevenueLoading: false,
  salesLeaderboardRevenueError: null,
  salesLeaderboardEstimated: emptyReport(),
  salesLeaderboardEstimatedLoading: false,
  salesLeaderboardEstimatedError: null,
  salesLeaderboardBooked: emptyReport(),
  salesLeaderboardBookedLoading: false,
  salesLeaderboardBookedError: null,
  salesLeaderboardBookedCount: emptyReport(),
  salesLeaderboardBookedCountLoading: false,
  salesLeaderboardBookedCountError: null,
  salesLeaderboardBookedByEstimate: emptyReport(),
  salesLeaderboardBookedByEstimateLoading: false,
  salesLeaderboardBookedByEstimateError: null,
  salesLeaderboardUserEmails: null,
  salesLeaderboardDeletedUserIds: null,
  salesLeaderboardUserEmailsError: null,
  salesLeaderboardUserEmailsFor: null,
};

// The distinct user ids of the leaderboard entries, as one comparable string.
const joinedUserIds = (entries: SalesLeaderboardEntry[]): string =>
  [ ...new Set(entries.map((e) => e.userId).filter(Boolean)) ].sort().join(',');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parseNumeric = (val: string | number | undefined): number => {
  if (typeof val === 'number') return val;
  if (!val || val === 'None') return 0;
  // eslint-disable-next-line no-useless-escape
  return parseFloat(String(val).replace(/[^0-9.\-]/g, '')) || 0;
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

@Injectable()
export class DashboardSalesLeaderboardStore extends ComponentStore<SalesLeaderboardState> {

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly dataOnlyGQL: DataOnlyGQL,
    private readonly listUsersGQL: ListUsersGQL,
    private readonly graphqlModule: GraphQLModule,
    private readonly demoModeService: DemoModeService,
  ) {
    super(initialState);
  }

  // --- Raw data selectors ---

  private readonly salesLeaderboard$ = this.select((state) => state.salesLeaderboard);
  private readonly salesLeaderboardLoading$ = this.select((state) => state.salesLeaderboardLoading);
  private readonly salesLeaderboardError$ = this.select((state) => state.salesLeaderboardError);

  private readonly salesLeaderboardRevenue$ = this.select((state) => state.salesLeaderboardRevenue);
  private readonly salesLeaderboardRevenueLoading$ = this.select((state) => state.salesLeaderboardRevenueLoading);
  private readonly salesLeaderboardRevenueError$ = this.select((state) => state.salesLeaderboardRevenueError);

  private readonly salesLeaderboardEstimated$ = this.select((state) => state.salesLeaderboardEstimated);
  private readonly salesLeaderboardEstimatedLoading$ = this.select((state) => state.salesLeaderboardEstimatedLoading);
  private readonly salesLeaderboardEstimatedError$ = this.select((state) => state.salesLeaderboardEstimatedError);

  private readonly salesLeaderboardBooked$ = this.select((state) => state.salesLeaderboardBooked);
  private readonly salesLeaderboardBookedLoading$ = this.select((state) => state.salesLeaderboardBookedLoading);
  private readonly salesLeaderboardBookedError$ = this.select((state) => state.salesLeaderboardBookedError);

  private readonly salesLeaderboardBookedCount$ = this.select((state) => state.salesLeaderboardBookedCount);
  private readonly salesLeaderboardBookedCountLoading$ = this.select((state) => state.salesLeaderboardBookedCountLoading);
  private readonly salesLeaderboardBookedCountError$ = this.select((state) => state.salesLeaderboardBookedCountError);

  private readonly salesLeaderboardBookedByEstimate$ = this.select((state) => state.salesLeaderboardBookedByEstimate);
  private readonly salesLeaderboardBookedByEstimateLoading$ = this.select((state) => state.salesLeaderboardBookedByEstimateLoading);
  private readonly salesLeaderboardBookedByEstimateError$ = this.select((state) => state.salesLeaderboardBookedByEstimateError);
  private readonly salesLeaderboardUserEmails$ = this.select((state) => state.salesLeaderboardUserEmails);
  private readonly salesLeaderboardDeletedUserIds$ = this.select((state) => state.salesLeaderboardDeletedUserIds);
  private readonly salesLeaderboardUserEmailsError$ = this.select((state) => state.salesLeaderboardUserEmailsError);
  private readonly salesLeaderboardUserEmailsFor$ = this.select((state) => state.salesLeaderboardUserEmailsFor);

  private readonly salesLeaderboardLeads$ = this.select((state) => state.salesLeaderboardLeads);
  private readonly salesLeaderboardLeadsLoading$ = this.select((state) => state.salesLeaderboardLeadsLoading);
  private readonly salesLeaderboardLeadsError$ = this.select((state) => state.salesLeaderboardLeadsError);

  private readonly salesLeaderboardCancellation$ = this.select((state) => state.salesLeaderboardCancellation);
  private readonly salesLeaderboardCancellationLoading$ = this.select((state) => state.salesLeaderboardCancellationLoading);
  private readonly salesLeaderboardCancellationError$ = this.select((state) => state.salesLeaderboardCancellationError);

  // --- Reload triggers (keyed off shared-store observables) ---

  private readonly reloadSalesLeaderboardTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.salesLeaderboardSortKey$,
    this.dashboardStore.salesLeaderboardSortDesc$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, sortKey, sortDesc, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, sortKey, sortDesc, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardRevenueTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardEstimatedTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardBookedTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardBookedCountTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardBookedByEstimateTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardLeadsTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  private readonly reloadSalesLeaderboardCancellationTrigger$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.routeActivated$,
    this.graphqlModule.zone,
    this.dashboardStore.reloadKey$,
    (currency, { start, end }, role, path, zone, _key) => {
      if (!zone) return null;
      return { start, end, currency, role, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'sales-leaderboard'),
  );

  // --- Query params selector ---

  private readonly salesLeaderboardQueryParams$ = this.dashboardStore.select(
    this.dashboardStore.currency$,
    this.dashboardStore.unixPeriod$,
    this.dashboardStore.role$,
    this.dashboardStore.salesLeaderboardSortKey$,
    this.dashboardStore.salesLeaderboardSortDesc$,
    this.dashboardStore.timezone$,
    this.graphqlModule.zone,
    (currency, { start, end }, role, sortKey, sortDesc, timezone, zone) => {
      if (!zone) return null;
      const queryVars = this.dashboardStore.getQueryVars({
        start,
        end,
        currency,
        report: 'salesLeaderboard',
        opts: { role, salesLeaderboardSortKey: sortKey, salesLeaderboardSortDesc: sortDesc },
      });
      const params = this.dashboardStore.queryVarsToQueryParams(queryVars, timezone) as Record<string, string>;
      // Requirement: ticket 7279 - View Report and the name links open this query, so they leave out the same
      // closure reasons (customer no-shows on the Estimator tab); the shared encoder doesn't carry them.
      const excludedReasons = queryVars.filter?.job?.excludeClosedReasons;
      return excludedReasons?.length ? { ...params, excludeClosedReasons: excludedReasons.join(';') } : params;
    },
  ).pipe(filter(Boolean));

  // --- Private helpers ---

  private parseAgentKey(rawName: string): { key: string; objectId: string | null } {
    const isUnassigned = !rawName || rawName === 'None';
    if (isUnassigned) return { key: '__unassigned__', objectId: null };
    const parsed = parseEmbeddedUrl(rawName);
    if (parsed) {
      const parts = parsed.url.split('/');
      return { key: parts[2] || rawName, objectId: parts[2] || null };
    }
    const hyperlinkParsed = parseHyperlink(rawName);
    if (hyperlinkParsed) {
      const urlSegments = /^=HYPERLINK\("([^"]+)"/.exec(rawName);
      if (urlSegments) {
        try {
          const urlPath = new URL(urlSegments[1]).pathname;
          const parts = urlPath.split('/');
          return { key: parts[2] || rawName, objectId: parts[2] || null };
        } catch {}
      }
    }
    return { key: rawName, objectId: null };
  }

  private extractAgentColumnMap(
    data: ReportData,
    role: SalesLeaderboardRole,
    dataColumns: readonly string[],
    targetColumn: string,
  ): Map<string, number> {
    const columnKeys = [ role, ...dataColumns ];
    const result = new Map<string, number>();
    for (const row of data.rows) {
      const { key } = this.parseAgentKey(row[0] || '');
      const idx = columnKeys.indexOf(targetColumn);
      const val = idx >= 0 ? parseNumeric(row[idx]) : 0;
      result.set(key, (result.get(key) || 0) + val);
    }
    return result;
  }

  // --- Entries selector (single source of truth for parsed/aggregated data) ---

  private readonly salesLeaderboardEntries$ = this.select(
    this.salesLeaderboard$,
    this.salesLeaderboardLeads$,
    this.salesLeaderboardCancellation$,
    this.salesLeaderboardRevenue$,
    this.salesLeaderboardEstimated$,
    this.salesLeaderboardBooked$,
    this.salesLeaderboardBookedCount$,
    this.salesLeaderboardBookedByEstimate$,
    this.dashboardStore.role$,
    this.dashboardStore.currency$,
    (data, leadsData, cancellationData, revenueData, estimatedData,
     bookedData, bookedCountData, bookedByEstimateData, role, selectedCurrency): SalesLeaderboardEntry[] => {
      const columnKeys = [ role, ...SALES_LEADERBOARD_DATA_COLUMNS ];
      const fallbackCurrency = selectedCurrency === 'All' ? baseEnvironment.defaultCurrency : selectedCurrency;
      // Every leaderboard report carries a Currency column (the zone's currency when "All" is selected).
      const rowCurrency = (report: ReportData, row: string[]) => {
        const idx = report.headers?.findIndex((h) => h.toLowerCase() === 'currency') ?? -1;
        return idx >= 0 && typeof row[idx] === 'string' && row[idx].length === 3 ? row[idx] : fallbackCurrency;
      };

      const byKey = new Map<string, SalesLeaderboardEntry>();

      // `row` is a salesLeaderboard row; without one the entry starts with zeroed data columns.
      const addEntry = (rawName: string, currency: string, row?: string[]) => {
        const isUnassigned = !rawName || rawName === 'None';
        const parsed = isUnassigned ? null : parseEmbeddedUrl(rawName);
        let hyperlinkParsed: ReturnType<typeof parseHyperlink> = null;
        if (!parsed && !isUnassigned) {
          hyperlinkParsed = parseHyperlink(rawName);
        }
        let entryName: string;
        let entryObjectId: string | null = null;
        let entryObjectType: string | null = null;
        if (isUnassigned) {
          entryName = 'Unassigned';
        } else if (parsed) {
          entryName = parsed.before || rawName;
          const parts = parsed.url.split('/');
          entryObjectType = parts[1] || null;
          entryObjectId = parts[2] || null;
        } else if (hyperlinkParsed) {
          entryName = hyperlinkParsed.text || rawName;
          const urlSegments = /^=HYPERLINK\("([^"]+)"/.exec(rawName);
          if (urlSegments) {
            try {
              const urlPath = new URL(urlSegments[1]).pathname;
              const parts = urlPath.split('/');
              entryObjectType = parts[1] || null;
              entryObjectId = parts[2] || null;
            } catch {}
          }
        } else {
          entryName = rawName;
        }
        const key = isUnassigned ? '__unassigned__' : (entryObjectId || rawName);

        const existing = byKey.get(key);

        const entry: SalesLeaderboardEntry = existing ?? {
          name: entryName,
          embeddedName: isUnassigned ? 'Unassigned' : rawName,
          isUnassigned,
          userId: entryObjectType === 'user' && entryObjectId ? entryObjectId : null,
          currency,
        };

        for (const col of SALES_LEADERBOARD_DATA_COLUMNS) {
          const idx = columnKeys.indexOf(col);
          const val = idx >= 0 && row ? parseNumeric(row[idx]) : 0;
          entry[col] = (typeof existing?.[col] === 'number' ? existing[col] as number : 0) + val;
        }

        if (!existing) {
          byKey.set(key, entry);
        }
      };

      for (const row of data.rows) {
        addEntry(row[0] || '', rowCurrency(data, row), row);
      }

      // The salesLeaderboard rows follow event end dates, so someone whose leads, bookings, estimates or
      // revenue fall in the period without an event ending in it would get no row and drop out of the
      // totals. Give every person the other reports name an entry too (ticket 7056 review).
      const otherAgentCells = [ leadsData, cancellationData, revenueData, estimatedData, bookedData, bookedCountData,
        bookedByEstimateData ]
        .flatMap((report) => report.rows.map((row) => ({ cell: row[0] || '', currency: rowCurrency(report, row) })));
      for (const { cell, currency } of otherAgentCells) {
        if (!byKey.has(this.parseAgentKey(cell).key)) {
          addEntry(cell, currency);
        }
      }

      // Lead count: distinct job count from the jobCreatedAt-based leads query
      const leadCountByObjectId = this.extractAgentColumnMap(
        leadsData, role, SALES_LEADERBOARD_LEADS_DATA_COLUMNS, 'jobCount',
      );
      // Requirement: ticket 7279 - Net Conversion counts the bookings among those same leads, not the moves
      // ending in the period, which mostly came from earlier leads and could pass 100%.
      const qualifiedBookingCountByObjectId = this.extractAgentColumnMap(
        leadsData, role, SALES_LEADERBOARD_LEADS_DATA_COLUMNS, 'connectedQualifiedBookingCount',
      );

      // Cancellation data: event count and cancelled event count from the
      // dedicated query that covers all event statuses (not just completed).
      const cancellationEventCountByObjectId = this.extractAgentColumnMap(
        cancellationData, role, [ 'eventCount', 'cancelledEventCount' ], 'eventCount',
      );
      const cancellationCancelledCountByObjectId = this.extractAgentColumnMap(
        cancellationData, role, [ 'eventCount', 'cancelledEventCount' ], 'cancelledEventCount',
      );

      const revByObjectId = this.extractAgentColumnMap(
        revenueData, role, SALES_LEADERBOARD_REVENUE_DATA_COLUMNS, 'discountedSubTotal',
      );
      const estByObjectId = this.extractAgentColumnMap(
        estimatedData, role, SALES_LEADERBOARD_ESTIMATED_DATA_COLUMNS, 'discountedSubTotal',
      );
      const estimatedCountByObjectId = this.extractAgentColumnMap(
        estimatedData, role, SALES_LEADERBOARD_ESTIMATED_DATA_COLUMNS, 'jobCount',
      );
      const bookedRevenueByObjectId = this.extractAgentColumnMap(
        bookedData, role, SALES_LEADERBOARD_BOOKED_DATA_COLUMNS, 'discountedSubTotal',
      );
      // Requirement: ticket 7056 - Discount % covers the jobs booked in the period (the same jobs as
      // Booked $$$), not the work performed in it.
      const subTotalByObjectId = this.extractAgentColumnMap(
        bookedData, role, SALES_LEADERBOARD_BOOKED_DATA_COLUMNS, 'subTotal',
      );
      const discountTotalByObjectId = this.extractAgentColumnMap(
        bookedData, role, SALES_LEADERBOARD_BOOKED_DATA_COLUMNS, 'discountTotal',
      );
      const bookedCountByObjectId = this.extractAgentColumnMap(
        bookedCountData, role, [ 'jobCount' ], 'jobCount',
      );
      // Requirement: ticket 7056 - Quote-to-close, and Booked on the Sales Agent tab, count the jobs
      // ESTIMATED in the period that are now booked, so a job estimated Sept 30 and booked Oct 7 counts for
      // September. Booked on the Estimator tab (Oct 20 meeting), Booked $$$ and AJS stay on the booking
      // date (sales-per-hour bonus), so they keep the count above.
      const bookedByEstimateCountByObjectId = this.extractAgentColumnMap(
        bookedByEstimateData, role, [ 'jobCount' ], 'jobCount',
      );

      return Array.from(byKey.values()).map((entry) => {
        const entryKey = entry.isUnassigned ? '__unassigned__' : (entry.userId || entry.name);
        const subTotal = subTotalByObjectId.get(entryKey) || 0;
        const discountTotal = discountTotalByObjectId.get(entryKey) || 0;
        const estimateCount = estimatedCountByObjectId.get(entryKey) || 0;
        const bookedCount = bookedCountByObjectId.get(entryKey) || 0;
        const bookedByEstimateCount = bookedByEstimateCountByObjectId.get(entryKey) || 0;
        const bookedRevenue = bookedRevenueByObjectId.get(entryKey) || 0;
        const leadCount = leadCountByObjectId.get(entryKey) || 0;
        const connectedQualifiedBookingCount = qualifiedBookingCountByObjectId.get(entryKey) || 0;

        // Cancellation counts from the dedicated cancellation query (all event statuses).
        const eventCount = cancellationEventCountByObjectId.get(entryKey) || 0;
        const cancelledEventCount = cancellationCancelledCountByObjectId.get(entryKey) || 0;

        entry.revenue = revByObjectId.get(entryKey) || 0;
        entry.estimatedRevenue = estByObjectId.get(entryKey) || 0;
        entry.estimateCount = estimateCount;
        entry.bookingCount = role === 'Estimator' ? bookedCount : bookedByEstimateCount;
        entry.bookingCountByBookingDate = bookedCount;
        entry.bookingCountByEstimateDate = bookedByEstimateCount;
        // salesLeaderboard (main query, eventEnd) only builds the row set so agents with
        // future/not-yet-completed events still show up (fix/sales-leaderboard-future-visibility).
        // Booked $$$ itself must come solely from salesLeaderboardBooked (jobBookingStageDate);
        // falling back to the eventEnd-based base value here previously let a job booked well
        // outside the period (e.g. booked in June for a September event) count as September's
        // Booked $$$/podium revenue. Zero — like bookingCount above — is the correct value when
        // nothing was actually booked in this period.
        entry.discountedSubTotal = bookedRevenue;
        entry.subTotal = subTotal;
        entry.discountTotal = discountTotal;
        entry.connectedLeadCount = leadCount;
        entry.connectedQualifiedBookingCount = connectedQualifiedBookingCount;
        entry.discountPercent = subTotal > 0 ? discountTotal / subTotal : 0;
        entry.ajs = bookedCount > 0 ? bookedRevenue / bookedCount : 0;
        entry.estimateConversionRate = estimateCount > 0 ? bookedByEstimateCount / estimateCount : 0;
        entry.netConversionRate = leadCount > 0 ? connectedQualifiedBookingCount / leadCount : 0;
        // Store raw counts so totals row can aggregate correctly.
        entry.cancellationEventCount = eventCount;
        entry.cancellationCancelledCount = cancelledEventCount;
        entry.cancellationRate = eventCount > 0 ? cancelledEventCount / eventCount : 0;

        return entry as SalesLeaderboardEntry;
      });
    },
  );

  // --- View model ---

  readonly salesLeaderboardViewModel$ = this.select(
    this.salesLeaderboardEntries$,
    this.salesLeaderboardLoading$,
    this.salesLeaderboardError$,
    this.salesLeaderboardLeadsLoading$,
    this.salesLeaderboardLeadsError$,
    this.salesLeaderboardCancellationLoading$,
    this.salesLeaderboardCancellationError$,
    this.salesLeaderboardRevenueLoading$,
    this.salesLeaderboardRevenueError$,
    this.salesLeaderboardEstimatedLoading$,
    this.salesLeaderboardEstimatedError$,
    this.salesLeaderboardBookedLoading$,
    this.salesLeaderboardBookedError$,
    this.salesLeaderboardBookedCountLoading$,
    this.salesLeaderboardBookedCountError$,
    this.salesLeaderboardBookedByEstimateLoading$,
    this.salesLeaderboardBookedByEstimateError$,
    this.salesLeaderboardUserEmails$,
    this.salesLeaderboardDeletedUserIds$,
    this.salesLeaderboardUserEmailsError$,
    this.salesLeaderboardUserEmailsFor$,
    this.dashboardStore.role$,
    this.dashboardStore.salesLeaderboardSortKey$,
    this.dashboardStore.salesLeaderboardSortDesc$,
    this.dashboardStore.displayPeriod$,
    this.dashboardStore.lastReload$,
    this.salesLeaderboardQueryParams$,
    (
      entries, loading, error, leadsLoading, leadsError,
      cancellationLoading, cancellationError,
      revenueLoading, revenueError,
      estimatedLoading, estimatedError, bookedLoading, bookedError,
      bookedCountLoading, bookedCountError, bookedByEstimateLoading, bookedByEstimateError,
      userEmails, deletedUserIds, userEmailsError, userEmailsFor, role, sortKey, sortDesc, displayPeriod, lastReload, queryParams,
    ) => {
      const headers: { key: string; label: string }[] = [
        { key: 'name', label: role },
        ...SALES_LEADERBOARD_COLUMNS,
      ];

      const sortedEntries = [...entries].sort((a, b) => {
        const va = (a[sortKey] as number) ?? 0;
        const vb = (b[sortKey] as number) ?? 0;
        return sortDesc ? vb - va : va - vb;
      });

      // -----------------------------------------------------------------------
      // Row formatter (reused for data rows, totals row, and mean row)
      // -----------------------------------------------------------------------
      const defaultCurrency = sortedEntries.find((e) => e.currency)?.currency || 'USD';

      const formatCell = (col: typeof SALES_LEADERBOARD_COLUMNS[number], val: number, currency = defaultCurrency): string => {
        if (col.format === 'percentage') {
          if (col.key === 'discountPercent' || col.key === 'cancellationRate') {
            return `${Math.round(val * 100)}%`;
          }
          return `${(val * 100).toFixed(1)}%`;
        }
        if (col.format === 'currency') {
          const rounded = Math.round(val);
          return currency
            ? new Intl.NumberFormat('en-US', {
                style: 'currency', currency,
                minimumFractionDigits: 0, maximumFractionDigits: 0,
              }).format(rounded)
            : String(rounded);
        }
        // Requirement: ticket 7056 - counts are whole numbers except in the Mean row, shown to one decimal.
        return Number.isInteger(val) ? String(val) : val.toFixed(1);
      };

      // Requirement: ticket 7056 - people who left get no row (or podium place), but their work stays
      // in the totals below. Unassigned gets no row either: it is nobody's work, so it never counted in
      // the totals; the note under the table says how many leads it holds.
      // Someone has left when their name says so or (MR review) their account is deleted, once the lookup has loaded.
      const deleted = new Set(deletedUserIds ?? []);
      const hasLeft = (e: SalesLeaderboardEntry) => isFormerStaffName(e.name) || Boolean(e.userId && deleted.has(e.userId));
      // Karve / Move Right staff are hidden too (Oct 21 meeting), once their emails have loaded.
      const isHiddenStaff = (e: SalesLeaderboardEntry) => Boolean(userEmails && e.userId && isStaffEmail(userEmails[e.userId]));
      // Until the emails load nobody can tell staff apart, so no person rows show (the table stays loading).
      // If the lookup fails, every row but the renamed ones shows with the error icon rather than an empty leaderboard.
      const emailsPending = userEmailsFor !== joinedUserIds(entries);
      const shownEntries = emailsPending
        ? []
        : sortedEntries.filter((e) => !e.isUnassigned && !hasLeft(e) && !isHiddenStaff(e));
      const unassignedLeads = Number(sortedEntries.find((e) => e.isUnassigned)?.connectedLeadCount) || 0;

      const rows = shownEntries.map((entry) => {
        const row: string[] = [
          entry.name,
          ...SALES_LEADERBOARD_COLUMNS.map((col) => {
            const val = entry[col.key];
            return typeof val === 'number'
              ? formatCell(col, val, entry.currency as string || defaultCurrency)
              : (col.format === 'percentage' ? '0.0%' : col.format === 'currency' ? '$0' : '0');
          }),
        ];
        return row;
      });

      // -----------------------------------------------------------------------
      // Totals & mean rows (exclude Unassigned). Totals keep people who left; the mean is the
      // benchmark for the people shown, so it leaves them out.
      // -----------------------------------------------------------------------
      const ranked = sortedEntries.filter((e) => !e.isUnassigned);
      const shownRanked = shownEntries.filter((e) => !e.isUnassigned);
      // Bots are only known by email, so there is no Mean row until the emails have loaded (or when the
      // lookup fails) rather than one that quietly includes them.
      const meanEntries = userEmails
        ? shownRanked.filter((e) => !isLeftOutOfMean(e.userId ? userEmails[e.userId] : null))
        : [];
      const leftOutOfMean = userEmails ? shownRanked.filter((e) => !meanEntries.includes(e)) : [];
      // Divides by 1 when nobody is in the mean (e.g. only bots shown); the label still says 0.
      const agentCount = meanEntries.length || 1;
      const formerStaff = ranked.filter(hasLeft);
      const hiddenStaff = ranked.filter((e) => !hasLeft(e) && isHiddenStaff(e));

      const sumLeads = ranked.reduce((s, e) => s + (Number(e.connectedLeadCount) || 0), 0);
      const sumEstimateCount = ranked.reduce((s, e) => s + (Number(e.estimateCount) || 0), 0);
      const sumEstimatedRevenue = ranked.reduce((s, e) => s + (Number(e.estimatedRevenue) || 0), 0);
      const sumBookingCount = ranked.reduce((s, e) => s + (Number(e.bookingCount) || 0), 0);
      const sumBookingCountByBookingDate = ranked.reduce((s, e) => s + (Number(e.bookingCountByBookingDate) || 0), 0);
      const sumBookingCountByEstimateDate = ranked.reduce((s, e) => s + (Number(e.bookingCountByEstimateDate) || 0), 0);
      const sumBookedRevenue = ranked.reduce((s, e) => s + (Number(e.discountedSubTotal) || 0), 0);
      const sumRevenue = ranked.reduce((s, e) => s + (Number(e.revenue) || 0), 0);
      const sumQualifiedBookings = ranked.reduce((s, e) => s + (Number(e.connectedQualifiedBookingCount) || 0), 0);
      const sumEventCount = ranked.reduce((s, e) => s + (Number(e.cancellationEventCount) || 0), 0);
      const sumCancelledEventCount = ranked.reduce((s, e) => s + (Number(e.cancellationCancelledCount) || 0), 0);
      const sumSubTotal = ranked.reduce((s, e) => s + (Number(e.subTotal) || 0), 0);
      const sumDiscountTotal = ranked.reduce((s, e) => s + (Number(e.discountTotal) || 0), 0);

      const totalsValues: Record<string, number> = {
        connectedLeadCount: sumLeads,
        estimateCount: sumEstimateCount,
        estimatedRevenue: sumEstimatedRevenue,
        bookingCount: sumBookingCount,
        discountedSubTotal: sumBookedRevenue,
        ajs: sumBookingCountByBookingDate > 0 ? sumBookedRevenue / sumBookingCountByBookingDate : 0,
        netConversionRate: sumLeads > 0 ? sumQualifiedBookings / sumLeads : 0,
        estimateConversionRate: sumEstimateCount > 0 ? sumBookingCountByEstimateDate / sumEstimateCount : 0,
        discountPercent: sumSubTotal > 0 ? sumDiscountTotal / sumSubTotal : 0,
        cancellationRate: sumEventCount > 0 ? sumCancelledEventCount / sumEventCount : 0,
        revenue: sumRevenue,
      };

      // Requirement: ticket 7056 (Oct 20 meeting) - the mean leaves out bots, staff and people who left,
      // so its rates come from the same people as its counts (same formulas as the totals).
      const sumShown = (key: string) => meanEntries.reduce((s, e) => s + (Number(e[key]) || 0), 0);
      const ratio = (numerator: number, denominator: number) => (denominator > 0 ? numerator / denominator : 0);
      const meanValues: Record<string, number> = {
        connectedLeadCount: sumShown('connectedLeadCount') / agentCount,
        estimateCount: sumShown('estimateCount') / agentCount,
        estimatedRevenue: sumShown('estimatedRevenue') / agentCount,
        bookingCount: sumShown('bookingCount') / agentCount,
        discountedSubTotal: sumShown('discountedSubTotal') / agentCount,
        ajs: ratio(sumShown('discountedSubTotal'), sumShown('bookingCountByBookingDate')),
        netConversionRate: ratio(sumShown('connectedQualifiedBookingCount'), sumShown('connectedLeadCount')),
        estimateConversionRate: ratio(sumShown('bookingCountByEstimateDate'), sumShown('estimateCount')),
        discountPercent: ratio(sumShown('discountTotal'), sumShown('subTotal')),
        cancellationRate: ratio(sumShown('cancellationCancelledCount'), sumShown('cancellationEventCount')),
        revenue: sumShown('revenue') / agentCount,
      };

      const totalsRow = [ 'Total', ...SALES_LEADERBOARD_COLUMNS.map((col) => formatCell(col, totalsValues[col.key] ?? 0)) ];
      const meanRow = userEmails
        ? [
          `Mean (${meanEntries.length})`,
          ...SALES_LEADERBOARD_COLUMNS.map((col) => formatCell(col, meanValues[col.key] ?? 0)),
        ]
        : null;

      // -----------------------------------------------------------------------
      // Podium — top 3 by bookingCount, always sorted by bookingCount desc
      // -----------------------------------------------------------------------
      const getInitials = (name: string): string => {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
      };

      const podiumSource = [ ...shownRanked ]
        .sort((a, b) => (Number(b.discountedSubTotal) || 0) - (Number(a.discountedSubTotal) || 0))
        .slice(0, 3);

      const podium = podiumSource.map((entry, idx) => {
        const currency = entry.currency as string || defaultCurrency;
        const revenue = Number(entry.discountedSubTotal) || 0;
        // Paired with Booked $$$, so it counts jobs by booking date like AJS.
        const jobs = Number(entry.bookingCountByBookingDate) || 0;
        const avg = jobs > 0 ? revenue / jobs : 0;
        const lead = idx === 0 && podiumSource[1]
          ? revenue - (Number(podiumSource[1].discountedSubTotal) || 0)
          : null;
        return {
          rank: (idx + 1) as 1 | 2 | 3,
          name: entry.name,
          initials: getInitials(entry.name),
          revenue: formatCell({ key: 'discountedSubTotal', label: '', format: 'currency', tooltip: '' }, revenue, currency),
          jobs,
          avg: formatCell({ key: 'ajs', label: '', format: 'currency', tooltip: '' }, avg, currency),
          leadOver: lead !== null && lead > 0
            ? formatCell({ key: 'discountedSubTotal', label: '', format: 'currency', tooltip: '' }, lead, currency) + ' ahead of #2'
            : null,
        };
      });

      const rankableCount = shownRanked.length;
      const tableTitle = `All ${role}s`;

      // Same order as `rows`: the drill-downs find a row's user by index.
      const userIds = shownEntries.map((e) => e.userId);
      const combinedLoading =
        loading || leadsLoading || cancellationLoading ||
        revenueLoading || estimatedLoading || bookedLoading || bookedCountLoading || bookedByEstimateLoading ||
        emailsPending;
      const combinedError = error || leadsError || cancellationError || revenueError || estimatedError || bookedError
        || bookedCountError || bookedByEstimateError || userEmailsError;

      return {
        tableTitle,
        role,
        sortKey,
        sortDesc,
        headers,
        rows,
        totalsRow,
        meanRow,
        podium,
        userIds,
        rankableCount,
        loading: combinedLoading,
        error: combinedError,
        // Empty only when nobody but Unassigned has activity: if everyone with activity is hidden, the Total row
        // and the note still say what happened in the period.
        isEmpty: !ranked.length && !combinedLoading,
        hiddenRowsNote: buildHiddenRowsNote(
          formerStaff.length, unassignedLeads, role, leftOutOfMean.length, hiddenStaff.length,
        ),
        hiddenRowsTooltip: buildHiddenRowsTooltip({
          left: formerStaff.map((e) => e.name),
          staff: hiddenStaff.map((e) => e.name),
          bots: leftOutOfMean.map((e) => e.name),
        }),
        displayPeriod,
        lastReload,
        queryParams,
      };
    },
  );

  // --- Effects ---

  // Requirement: ticket 7056 - the leaderboard rows carry names and ids only, so the emails that pick out
  // staff rows and bots are looked up once per set of people. Demo rows are not real users.
  readonly loadSalesLeaderboardUserEmails = this.effect(() => combineLatest([
    this.salesLeaderboardEntries$.pipe(
      map(joinedUserIds),
      distinctUntilChanged(),
    ),
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ joinedIds, isDemo ]) => {
      if (isDemo || !joinedIds) {
        this.patchState({
          salesLeaderboardUserEmails: {}, salesLeaderboardDeletedUserIds: [], salesLeaderboardUserEmailsError: null,
          salesLeaderboardUserEmailsFor: joinedIds,
        });
        return EMPTY;
      }
      // Clearing the ids too keeps an earlier lookup for the same people from passing as this one.
      this.patchState({
        salesLeaderboardUserEmails: null, salesLeaderboardDeletedUserIds: null, salesLeaderboardUserEmailsError: null,
        salesLeaderboardUserEmailsFor: null,
      });
      return this.listUsersGQL.fetch({
        filter: { userIds: joinedIds.split(','), zoneDir: ZoneDir.Any, showDeactivated: true },
        limit: -1,
      }).pipe(tapResponse({
        next: (res) => {
          const emails: Record<string, string> = {};
          const deletedIds: string[] = [];
          for (const user of res.data.usersv2?.users ?? []) {
            if (user?.email) {
              emails[user.id] = user.email;
            }
            if (user?.deletedAt) {
              deletedIds.push(user.id);
            }
          }
          // A viewer whose users.view is restricted can get some people back without an email. Telling
          // staff and bots apart from part of the list would be wrong without saying so, so it is shown
          // like a failed lookup.
          const missing = joinedIds.split(',').filter((id) => !emails[id]).length;
          if (missing) {
            this.patchState({
              salesLeaderboardUserEmails: null,
              salesLeaderboardDeletedUserIds: null,
              salesLeaderboardUserEmailsError:
                `Staff, bots and deleted accounts could not be identified: no email for ${missing} of the people listed`,
              salesLeaderboardUserEmailsFor: joinedIds,
            });
            return;
          }
          this.patchState({
            salesLeaderboardUserEmails: emails, salesLeaderboardDeletedUserIds: deletedIds, salesLeaderboardUserEmailsFor: joinedIds,
          });
        },
        // The Mean row stays hidden and staff rows show; the error icon says why.
        error: (err: ApolloError) => this.patchState({
          salesLeaderboardUserEmails: null,
          salesLeaderboardDeletedUserIds: null,
          salesLeaderboardUserEmailsError: `Staff, bots and deleted accounts could not be identified: ${getDashboardErrorMessage(err)}`,
          salesLeaderboardUserEmailsFor: joinedIds,
        }),
      }));
    }),
  ));

  readonly reloadSalesLeaderboard = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboard: emptyReport(),
          salesLeaderboardLoading: false,
          salesLeaderboardError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role, sortKey, sortDesc }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboard',
      opts: { role, salesLeaderboardSortKey: sortKey, salesLeaderboardSortDesc: sortDesc },
    })),
    tap(() => this.patchState({
      salesLeaderboardLoading: true,
      salesLeaderboardError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboard: { headers, rows },
          salesLeaderboardLoading: false,
        });
        this.dashboardStore.setLastReload(currentTimeSeconds());
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardLoading: false,
          salesLeaderboardError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardRevenue = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardRevenueTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardRevenue: emptyReport(),
          salesLeaderboardRevenueLoading: false,
          salesLeaderboardRevenueError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardRevenue',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardRevenueLoading: true,
      salesLeaderboardRevenueError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardRevenue: { headers, rows },
          salesLeaderboardRevenueLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardRevenueLoading: false,
          salesLeaderboardRevenueError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardEstimated = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardEstimatedTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardEstimated: emptyReport(),
          salesLeaderboardEstimatedLoading: false,
          salesLeaderboardEstimatedError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardEstimated',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardEstimatedLoading: true,
      salesLeaderboardEstimatedError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardEstimated: { headers, rows },
          salesLeaderboardEstimatedLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardEstimatedLoading: false,
          salesLeaderboardEstimatedError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardBooked = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardBookedTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardBooked: emptyReport(),
          salesLeaderboardBookedLoading: false,
          salesLeaderboardBookedError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardBooked',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardBookedLoading: true,
      salesLeaderboardBookedError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardBooked: { headers, rows },
          salesLeaderboardBookedLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardBookedLoading: false,
          salesLeaderboardBookedError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardBookedCount = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardBookedCountTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardBookedCount: emptyReport(),
          salesLeaderboardBookedCountLoading: false,
          salesLeaderboardBookedCountError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardBookedCount',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardBookedCountLoading: true,
      salesLeaderboardBookedCountError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardBookedCount: { headers, rows },
          salesLeaderboardBookedCountLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardBookedCountLoading: false,
          salesLeaderboardBookedCountError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardBookedByEstimate = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardBookedByEstimateTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardBookedByEstimate: emptyReport(),
          salesLeaderboardBookedByEstimateLoading: false,
          salesLeaderboardBookedByEstimateError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardBookedByEstimate',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardBookedByEstimateLoading: true,
      salesLeaderboardBookedByEstimateError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardBookedByEstimate: { headers, rows },
          salesLeaderboardBookedByEstimateLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardBookedByEstimateLoading: false,
          salesLeaderboardBookedByEstimateError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardCancellation = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardCancellationTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardCancellation: emptyReport(),
          salesLeaderboardCancellationLoading: false,
          salesLeaderboardCancellationError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardCancellation',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardCancellationLoading: true,
      salesLeaderboardCancellationError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardCancellation: { headers, rows },
          salesLeaderboardCancellationLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardCancellationLoading: false,
          salesLeaderboardCancellationError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadSalesLeaderboardLeads = this.effect(() => combineLatest([
    this.reloadSalesLeaderboardLeadsTrigger$,
    this.demoModeService.isDemo$,
  ]).pipe(
    switchMap(([ trigger, isDemo ]) => {
      if (isDemo) {
        this.patchState({
          salesLeaderboardLeads: emptyReport(),
          salesLeaderboardLeadsLoading: false,
          salesLeaderboardLeadsError: null,
        });
        return EMPTY;
      }
      return of(trigger);
    }),
    map(({ currency, start, end, role }) => this.dashboardStore.getQueryVars({
      start,
      end,
      currency,
      report: 'salesLeaderboardLeads',
      opts: { role },
    })),
    tap(() => this.patchState({
      salesLeaderboardLeadsLoading: true,
      salesLeaderboardLeadsError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          salesLeaderboardLeads: { headers, rows },
          salesLeaderboardLeadsLoading: false,
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          salesLeaderboardLeadsLoading: false,
          salesLeaderboardLeadsError: getDashboardErrorMessage(err),
        });
      },
    }))),
  ));
}
